/**
 * Verse Analysis panel — Liquid Glass bottom drawer, sibling of the
 * Strong's panel. Shows the KJV-vs-NASB problem rating, the prose
 * analysis, and the diff-highlighted word comparison.
 *
 * `isSplit` (Strong's panel open) shifts it into the left half so both
 * drawers live side by side; on phones they stack instead (see CSS).
 */

import type { BibleLocation, VariantAnalysis } from '@skjv/common';
import { CloseIcon } from '@components/icons';
import { useAnimationState } from '@/hooks/use-animation-state';
import { severityFor } from './severity';
import { AnalysisSummaries } from './analysis-summaries';
import { ComparisonStrips } from './comparison-strips';
import { NasbDisclaimer } from './nasb-disclaimer';

interface VerseAnalysisPanelProps {
  analysis: VariantAnalysis;
  location: BibleLocation;
  isVisible: boolean;
  isSplit: boolean;
  onClose: () => void;
}

export function VerseAnalysisPanel({
  analysis,
  location,
  isVisible,
  isSplit,
  onClose,
}: VerseAnalysisPanelProps) {
  const { shouldRender, animationState } = useAnimationState(isVisible, {
    enterDelay: 500,
    exitDelay: 350,
  });

  if (!shouldRender) return null;

  const severity = severityFor(analysis.score);

  return (
    <div
      className={`verse-analysis-panel ${severity.className} ${animationState === 'entering' ? 'entering' : ''} ${animationState === 'exiting' ? 'closing' : ''} ${isSplit ? 'split-left' : ''}`}
    >
      <div className="verse-analysis-header">
        <severity.Icon className="verse-analysis-header-icon" filled />
        <div className="min-w-0 flex-1">
          <h3 className="verse-analysis-title">
            {analysis.reference} Textual Criticism
            {analysis.isHumanReviewed && <span className="variant-reviewed-badge">Reviewed</span>}
          </h3>
        </div>
        <button
          onClick={onClose}
          className="-m-1 self-start rounded-full p-2 text-stone-500 transition-all hover:bg-black/5 hover:text-stone-700 dark:text-stone-400 dark:hover:bg-white/10 dark:hover:text-stone-200"
          title="Close"
        >
          <CloseIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="verse-analysis-content">
        <AnalysisSummaries analysis={analysis} />
        <ComparisonStrips analysis={analysis} location={location} />
        <NasbDisclaimer reference={analysis.reference} />
      </div>
    </div>
  );
}
